import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { PageHero } from "@/components/PageHero";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAlacarteCart } from "@/hooks/useAlacarteCart";
import { useCheckout } from "@/hooks/useCheckout";
import { friendlyCartError } from "@/lib/mp2/cart-errors";
import type { DeliveryAddress } from "@/lib/public/mp2-alacarte-cart.functions";
import { cartPriceBreakdown } from "@/lib/public/mp2-alacarte-cart.functions";

export const Route = createFileRoute("/cart")({
  head: () => ({
    meta: [
      { title: "Your cart · Poonam Kapur Healthy Kitchen" },
      {
        name: "description",
        content:
          "Review the à la carte dishes in your cart, update quantities and continue when you're ready.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CartPage,
});

function cleanName(name: string) {
  return name.replace(/^\[DEMO\]\s*/i, "").trim();
}
function formatPrice(paise: number) {
  return `₹${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
function formatDate(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wd = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][dt.getUTCDay()];
  const mo = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][dt.getUTCMonth()];
  return `${wd}, ${dt.getUTCDate()} ${mo}`;
}

function CartPage() {
  const cart = useAlacarteCart();
  const checkout = useCheckout(cart.token);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const snapshot = cart.snapshot;
  const hasMealPlan = snapshot?.items.some(item => item.kind === "meal_plan") ?? false;
  const getBreakdown = useServerFn(cartPriceBreakdown);
  const totalsQuery = useQuery({
    queryKey: ["meal-plan-cart-breakdown", cart.token, snapshot],
    enabled: Boolean(cart.token && hasMealPlan),
    queryFn: () => getBreakdown({ data: { token: cart.token } }),
    staleTime: 0,
  });
  const totals = totalsQuery.data && !("error" in totalsQuery.data)
    ? totalsQuery.data : null;
  const totalsUnavailable = totalsQuery.isError ||
    Boolean(totalsQuery.data && "error" in totalsQuery.data);

  // Meal-plan lines carry their own first service day; prefer it over the
  // cart's bootstrap date, which may be a non-delivery day.
  const mealPlanStart =
    snapshot?.items.find((i) => i.kind === "meal_plan" && i.start_date)?.start_date ?? null;

  const withBusy = async (id: string, fn: () => Promise<unknown>) => {
    setBusyId(id);
    setFlash(null);
    try {
      const res = await fn();
      if (res && typeof res === "object" && "error" in res) {
        setFlash(friendlyCartError((res as { error: string }).error));
      }
    } catch (err) {
      setFlash(friendlyCartError((err as Error).message));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Cart"
        title="Review your à la carte order"
        intro="Check availability, quantities and pricing. Checkout will open in the next step."
      />
      <Section>
        {cart.isLoading ? (
          <div className="text-sm text-muted-foreground">Loading your cart…</div>
        ) : !snapshot || snapshot.items.length === 0 ? (
          <Alert>
            <AlertTitle>Your cart is empty</AlertTitle>
            <AlertDescription>
              Browse today's kitchen and add dishes to your cart.
              <div className="mt-3">
                <Link
                  to="/products"
                  className="inline-flex items-center rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground"
                >
                  Explore à la carte
                </Link>
              </div>
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="rounded-2xl border border-border/70 bg-card p-4 text-sm">
              <div className="flex flex-wrap justify-between gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                    {mealPlanStart ? "First delivery" : "Delivery date"}
                  </div>
                  <div className="font-medium">
                    {formatDate(mealPlanStart ?? snapshot.cart.delivery_date)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                    Status
                  </div>
                  <div className="font-medium capitalize">{snapshot.cart.status}</div>
                </div>
                <Link
                  to="/products"
                  className="text-primary underline underline-offset-4"
                >
                  Change area or date
                </Link>
              </div>
            </div>

            {snapshot.validation.length > 0 ? (
              <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <div className="font-medium">Some items need attention</div>
                <ul className="mt-1 list-disc pl-5">
                  {snapshot.validation.map((v) => (
                    <li key={v.item_id}>{v.reason.replace(/_/g, " ")}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {flash ? (
              <div className="mt-4 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-xs text-primary">
                {flash}
              </div>
            ) : null}

            <ul className="mt-6 divide-y divide-border/70 rounded-2xl border border-border/70 bg-card">
              {snapshot.items.map((it) => {
                const info =
                  it.kind === "dish" ? it.dish :
                  it.kind === "beverage" ? it.beverage :
                  it.kind === "soup" ? it.soup :
                  it.kind === "salad" ? it.salad :
                  it.kind === "addon" ? (it.addon ?? null) :
                  it.kind === "meal_plan" ? it.meal_plan : null;
                const eyebrow =
                  it.kind === "dish"
                    ? (it.meal_slot ?? "")
                    : it.kind === "beverage"
                      ? `Beverage${it.beverage?.size_label ? ` · ${it.beverage.size_label}` : ""}`
                      : it.kind === "soup"
                        ? "Soup"
                        : it.kind === "salad"
                          ? "Salad"
                          : it.kind === "addon"
                            ? "Add-on"
                          : `Meal Plan${it.days_count ? ` · ${it.days_count} days` : ""}`;
                const name = info
                  ? cleanName(
                      it.kind === "meal_plan"
                        ? (it.meal_plan?.title ?? "Meal Plan")
                        : ((info as { name: string }).name ?? "Item"),
                    )
                  : "Item";
                const img =
                  it.kind === "meal_plan"
                    ? it.meal_plan?.hero_image_url ?? null
                    : (info as { image_url?: string | null } | null)?.image_url ?? null;
                const isMealPlan = it.kind === "meal_plan";
                const displayQty = isMealPlan
                  ? (it.plan_quantity ?? it.quantity)
                  : it.quantity;
                const unitPrice = isMealPlan
                  ? (it.price_per_day_paise ?? it.unit_price_paise)
                  : it.unit_price_paise;
                const unitLabel = isMealPlan ? "per day" : "each";
                const lineTotal = isMealPlan
                  ? (it.total_price_paise ?? it.line_total_paise)
                  : it.line_total_paise;
                return (
                <li key={it.id} className="flex items-center gap-4 p-4">
                  {img ? (
                    <img
                      src={img}
                      alt={name}
                      loading="lazy"
                      onError={(e) => {
                        // Hide broken image so we don't render an empty box with a broken-icon.
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                      className="h-16 w-16 flex-none rounded-lg object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 flex-none rounded-lg bg-secondary/40" aria-hidden />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                      {eyebrow}
                    </div>
                    <div className="truncate font-medium">{name}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatPrice(unitPrice)} {unitLabel}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label="Decrease quantity"
                      onClick={() =>
                        isMealPlan
                          ? displayQty > 1
                            ? withBusy(it.id, () =>
                                cart.updateMealPlanQuantity(it.id, displayQty - 1),
                              )
                            : withBusy(it.id, () => cart.removeItem(it.id))
                          : it.quantity > 1
                            ? withBusy(it.id, () => cart.updateItem(it.id, it.quantity - 1))
                            : withBusy(it.id, () => cart.removeItem(it.id))
                      }
                      disabled={busyId === it.id}
                      className="h-8 w-8 rounded-full border border-border text-sm"
                    >
                      −
                    </button>
                    <span className="w-6 text-center text-sm font-medium">{displayQty}</span>
                    <button
                      type="button"
                      aria-label="Increase quantity"
                      onClick={() =>
                        isMealPlan
                          ? withBusy(it.id, () =>
                              cart.updateMealPlanQuantity(it.id, Math.min(10, displayQty + 1)),
                            )
                          : withBusy(it.id, () => cart.updateItem(it.id, Math.min(10, it.quantity + 1)))
                      }
                      disabled={busyId === it.id || displayQty >= 10}
                      className="h-8 w-8 rounded-full border border-border text-sm"
                    >
                      +
                    </button>
                  </div>
                  <div className="w-20 text-right text-sm font-semibold">
                    {formatPrice(lineTotal)}
                  </div>
                  <button
                    type="button"
                    onClick={() => withBusy(it.id, () => cart.removeItem(it.id))}
                    disabled={busyId === it.id}
                    className="ml-2 text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
                  >
                    Remove
                  </button>
                </li>
                );
              })}
            </ul>

            {/* Delivery address details removed from cart page - moved to checkout step */}

            <div className="mt-6 flex flex-col items-end gap-3 rounded-2xl border border-border/70 bg-card p-6">
              <div className="text-sm text-muted-foreground">
                {cart.itemCount} item{cart.itemCount === 1 ? "" : "s"}
              </div>
              {hasMealPlan ? (
                totalsUnavailable ? (
                  <p role="alert" className="text-sm text-muted-foreground">
                    Your total is unavailable. Please try again before checkout.
                  </p>
                ) : !totals || totalsQuery.isFetching ? (
                  <p role="status" className="text-sm text-muted-foreground">Updating total…</p>
                ) : (
                  <dl className="w-full max-w-sm space-y-2 text-sm">
                    <div className="flex justify-between gap-4"><dt>Food subtotal</dt><dd>{formatPrice(totals.itemsPaise)}</dd></div>
                    <div className="flex justify-between gap-4"><dt>Delivery charges</dt><dd>{formatPrice(totals.deliveryPaise)}</dd></div>
                    {totals.gstEnabled && (
                      <div className="flex justify-between gap-4"><dt>{totals.gstMode === "inclusive" ? "GST included" : "GST"}</dt><dd>{formatPrice(totals.gstPaise)}</dd></div>
                    )}
                    <div className="flex justify-between gap-4 border-t pt-2 font-semibold"><dt>Total before checkout discounts</dt><dd>{formatPrice(totals.totalPaise)}</dd></div>
                  </dl>
                )
              ) : (
                <div className="text-2xl font-semibold">{formatPrice(cart.totalPaise)}</div>
              )}
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/products"
                  className="inline-flex items-center rounded-full border border-primary bg-background px-5 py-2 text-sm font-medium text-primary"
                >
                  Continue shopping
                </Link>
                {checkout.isCheckoutEnabled ? (
                  <Link
                    to="/checkout"
                    className="inline-flex items-center rounded-full bg-primary px-6 py-2 text-sm font-medium text-primary-foreground"
                  >
                    {checkout.mode === "test"
                      ? "Proceed to Checkout (admin test)"
                      : checkout.mode === "uat"
                        ? "Proceed to Checkout (UAT test payments)"
                        : "Proceed to Checkout"}
                  </Link>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="inline-flex items-center rounded-full bg-primary/60 px-6 py-2 text-sm font-medium text-primary-foreground opacity-70"
                    title="Checkout is coming soon"
                  >
                    Checkout · coming soon
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => cart.clearCart()}
                className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                Clear cart
              </button>
            </div>
          </>
        )}
      </Section>
    </SiteLayout>
  );
}

